import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell, Moon, Sun, Monitor, Shield,
  Database, RefreshCw, Trash2, ChevronRight, Info,
  Wifi, ClipboardList,
  User, LogOut, Settings, Loader2, Globe
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { taskService } from '@/services/firebase/tasks'
import { ROUTES } from '@/constants'
import toast from 'react-hot-toast'

// ── local pref helpers ────────────────────────────────────────────────────────
const PREF_KEY = 'bsnl_settings'
interface Prefs {
  theme: 'light' | 'dark' | 'system'
  density: 'comfortable' | 'compact'
  notifyNewTask: boolean
  notifyStatusChange: boolean
  notifyApproval: boolean
  defaultPriority: 'low' | 'medium' | 'high' | 'urgent'
  autoRefreshInterval: number   // minutes, 0 = off
}
const DEFAULTS: Prefs = {
  theme: 'light',
  density: 'comfortable',
  notifyNewTask: true,
  notifyStatusChange: true,
  notifyApproval: true,
  defaultPriority: 'medium',
  autoRefreshInterval: 5,
}
const loadPrefs = (): Prefs => {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(PREF_KEY) ?? '{}') } }
  catch { return DEFAULTS }
}
const savePrefs = (p: Prefs) => localStorage.setItem(PREF_KEY, JSON.stringify(p))

// ── sub-components ────────────────────────────────────────────────────────────
const SectionCard = ({ title, subtitle, icon: Icon, iconColor, children }: {
  title: string; subtitle?: string; icon: React.ElementType; iconColor: string; children: React.ReactNode
}) => (
  <div className="bg-white rounded-3xl overflow-hidden"
    style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' }}>
    <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-50">
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${iconColor}`}>
        <Icon size={15} className="text-white" />
      </div>
      <div>
        <p className="text-sm font-black text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="divide-y divide-gray-50">{children}</div>
  </div>
)

const Toggle = ({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) => (
  <button onClick={() => onChange(!value)}
    className={`relative w-10 h-6 rounded-full transition-colors duration-200 shrink-0 ${value ? 'bg-indigo-500' : 'bg-gray-200'}`}>
    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${value ? 'translate-x-5' : 'translate-x-1'}`} />
  </button>
)

const Row = ({ label, sub, children }: { label: string; sub?: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between px-5 py-3.5 gap-4">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-gray-800">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
)

const SegmentPicker = <T extends string>({ options, value, onChange }: {
  options: { value: T; label: string; icon?: React.ElementType }[]
  value: T
  onChange: (v: T) => void
}) => (
  <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
    {options.map(opt => (
      <button key={opt.value} onClick={() => onChange(opt.value)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
          value === opt.value ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
        }`}>
        {opt.icon && <opt.icon size={11} />}{opt.label}
      </button>
    ))}
  </div>
)

const NavRow = ({ icon: Icon, label, sub, onClick, danger }: {
  icon: React.ElementType; label: string; sub?: string; onClick: () => void; danger?: boolean
}) => (
  <button onClick={onClick}
    className={`w-full flex items-center justify-between px-5 py-3.5 transition-colors text-left ${danger ? 'hover:bg-red-50/60' : 'hover:bg-gray-50/60'}`}>
    <div className="flex items-center gap-3">
      <Icon size={15} className={danger ? 'text-red-400' : 'text-gray-400'} />
      <div>
        <p className={`text-sm font-semibold ${danger ? 'text-red-600' : 'text-gray-800'}`}>{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
    <ChevronRight size={15} className="text-gray-300" />
  </button>
)

// ── main ─────────────────────────────────────────────────────────────────────
const SettingsPage = () => {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'admin'

  const [prefs, setPrefs] = useState<Prefs>(loadPrefs)
  const [seeding, setSeeding]   = useState(false)
  const [clearing, setClearing] = useState(false)

  useEffect(() => { savePrefs(prefs) }, [prefs])

  const set = <K extends keyof Prefs>(key: K, val: Prefs[K]) =>
    setPrefs(p => ({ ...p, [key]: val }))

  const handleSeedTasks = async () => {
    setSeeding(true)
    const uid = user?.uid ?? user?.id ?? ''
    const res = await taskService.seedSampleTasks(uid)
    if (res.success) toast.success('Sample tasks seeded successfully')
    else toast.error(res.error ?? 'Seeding failed')
    setSeeding(false)
  }

  const handleClearCache = () => {
    const keys = Object.keys(localStorage).filter(k => k !== 'bsnl-auth-storage' && k !== PREF_KEY)
    keys.forEach(k => localStorage.removeItem(k))
    toast.success(`Cleared ${keys.length} cached item${keys.length !== 1 ? 's' : ''}`)
    setClearing(false)
  }

  const handleLogout = () => { logout(); navigate(ROUTES.LOGIN) }

  return (
    <div className="max-w-xl mx-auto space-y-5 pb-10">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg, #080f1e 0%, #0d1b33 50%, #0a1628 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.35) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        </div>
        <div className="relative z-10 px-6 py-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', boxShadow: '0 0 20px rgba(99,102,241,0.4)' }}>
            <Settings size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-black">Settings</h1>
            <p className="text-blue-300/70 text-sm mt-0.5">
              {isAdmin ? 'Admin preferences & system controls' : 'App preferences & account options'}
            </p>
          </div>
        </div>
      </div>

      {/* ── Appearance ── */}
      <SectionCard title="Appearance" subtitle="Display and layout preferences" icon={Monitor} iconColor="bg-indigo-500">
        <Row label="Theme" sub="Choose your colour scheme">
          <SegmentPicker
            value={prefs.theme}
            onChange={v => set('theme', v)}
            options={[
              { value: 'light' as const,  label: 'Light',  icon: Sun     },
              { value: 'dark'  as const,  label: 'Dark',   icon: Moon    },
              { value: 'system'as const,  label: 'System', icon: Monitor },
            ]}
          />
        </Row>
        <Row label="Density" sub="Card and list spacing">
          <SegmentPicker
            value={prefs.density}
            onChange={v => set('density', v)}
            options={[
              { value: 'comfortable' as const, label: 'Comfortable' },
              { value: 'compact'     as const, label: 'Compact'     },
            ]}
          />
        </Row>
      </SectionCard>

      {/* ── Notifications ── */}
      <SectionCard title="Notifications" subtitle="Choose what alerts you receive" icon={Bell} iconColor="bg-violet-500">
        <Row label="New Task Assigned" sub="Notify when a task is assigned to you">
          <Toggle value={prefs.notifyNewTask} onChange={v => set('notifyNewTask', v)} />
        </Row>
        <Row label="Status Updates" sub="Notify on task status changes">
          <Toggle value={prefs.notifyStatusChange} onChange={v => set('notifyStatusChange', v)} />
        </Row>
        {isAdmin && (
          <Row label="Approval Requests" sub="Notify when a task needs admin approval">
            <Toggle value={prefs.notifyApproval} onChange={v => set('notifyApproval', v)} />
          </Row>
        )}
        <Row label="Auto-Refresh" sub="Poll for new data in the background">
          <SegmentPicker
            value={String(prefs.autoRefreshInterval) as any}
            onChange={v => set('autoRefreshInterval', Number(v))}
            options={[
              { value: '0'  as any, label: 'Off'  },
              { value: '5'  as any, label: '5 m'  },
              { value: '15' as any, label: '15 m' },
            ]}
          />
        </Row>
      </SectionCard>

      {/* ── Task defaults (admin only) ── */}
      {isAdmin && (
        <SectionCard title="Task Defaults" subtitle="Defaults for new tasks created by admins" icon={ClipboardList} iconColor="bg-blue-500">
          <Row label="Default Priority" sub="Pre-selected priority for new tasks">
            <select value={prefs.defaultPriority} onChange={e => set('defaultPriority', e.target.value as Prefs['defaultPriority'])}
              className="text-xs font-bold text-gray-700 bg-gray-100 border-0 rounded-xl px-3 py-2 outline-none cursor-pointer">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </Row>
        </SectionCard>
      )}

      {/* ── Data & sync ── */}
      <SectionCard
        title={isAdmin ? 'Data Management' : 'Offline & Sync'}
        subtitle={isAdmin ? 'Seed, export, and cache controls' : 'Manage local cache and connectivity'}
        icon={isAdmin ? Database : Wifi}
        iconColor={isAdmin ? 'bg-emerald-500' : 'bg-cyan-500'}
      >
        {isAdmin && (
          <NavRow
            icon={seeding ? Loader2 : Database}
            label="Seed Sample Tasks"
            sub="Insert demo tasks for all statuses"
            onClick={handleSeedTasks}
          />
        )}
        <button onClick={() => { setClearing(true); setTimeout(handleClearCache, 300) }}
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50/60 transition-colors text-left">
          <div className="flex items-center gap-3">
            {clearing
              ? <Loader2 size={15} className="text-gray-400 animate-spin" />
              : <Trash2 size={15} className="text-gray-400" />}
            <div>
              <p className="text-sm font-semibold text-gray-800">Clear Local Cache</p>
              <p className="text-xs text-gray-400 mt-0.5">Remove cached data (keeps login &amp; prefs)</p>
            </div>
          </div>
          <ChevronRight size={15} className="text-gray-300" />
        </button>
        <NavRow
          icon={RefreshCw}
          label="Force Reload"
          sub="Reload the app to apply latest changes"
          onClick={() => window.location.reload()}
        />
        {!isAdmin && (
          <Row label="Offline Mode" sub="Your data is available without internet">
            <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1.5 rounded-xl">
              <Wifi size={11} /> Ready
            </span>
          </Row>
        )}
      </SectionCard>

      {/* ── Account ── */}
      <SectionCard title="Account" subtitle="Profile and authentication" icon={User} iconColor="bg-slate-500">
        <NavRow
          icon={User}
          label="Edit Profile"
          sub="Update your name, phone, zone"
          onClick={() => navigate(ROUTES.PROFILE)}
        />
        {isAdmin && (
          <NavRow
            icon={Shield}
            label="Manage Engineers"
            sub="Add, edit or deactivate field engineers"
            onClick={() => navigate(ROUTES.ENGINEERS)}
          />
        )}
        <NavRow
          icon={Globe}
          label="Notifications"
          sub="View all alerts and updates"
          onClick={() => navigate(ROUTES.NOTIFICATIONS)}
        />
        <NavRow
          icon={LogOut}
          label="Sign Out"
          sub="Log out of this device"
          onClick={handleLogout}
          danger
        />
      </SectionCard>

      {/* ── About ── */}
      <SectionCard title="About" subtitle="App info and version" icon={Info} iconColor="bg-gray-400">
        <Row label="Application">
          <span className="text-xs font-bold text-gray-500">BSNL FTTH Support</span>
        </Row>
        <Row label="Version">
          <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">v1.0.0</span>
        </Row>
        <Row label="Role">
          <span className={`text-xs font-black px-2.5 py-1 rounded-full ${isAdmin ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
            {isAdmin ? 'Administrator' : 'Field Engineer'}
          </span>
        </Row>
        <Row label="Build">
          <span className="text-xs font-bold text-gray-400">
            {new Date().toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
          </span>
        </Row>
      </SectionCard>

    </div>
  )
}

export default SettingsPage
